/**
 * AWS Captcha 服务
 * 负责渲染和处理用户交互式验证码挑战
 */
import * as Sentry from '@sentry/nextjs';
import { CAPTCHA_URL } from '@/app/constants';
import { createControlledPromise } from './controlled-promise';
import { AWSWafError } from './error';
import { loadScript } from './utils';

interface CaptchaOptions {
  apiKey: string | undefined;
  onSuccess: () => void;
  onFailure: () => void;
  disableLanguageSelector?: boolean;
}

declare const AwsWafCaptcha: {
  renderCaptcha: (container: HTMLElement, options: CaptchaOptions) => void;
};

export const createCaptchaService = () => {
  let loadPromise: Promise<void> | null = null;
  let isLoaded = false;

  const ensureLoaded = async (): Promise<void> => {
    if (isLoaded) {
      return;
    }

    loadPromise ??= loadScript(CAPTCHA_URL)
      .then(() => {
        isLoaded = true;
      })
      .catch((error) => {
        loadPromise = null;
        throw error;
      });

    await loadPromise;
  };

  const renderAWSCaptcha = async (): Promise<void> => {
    try {
      await ensureLoaded();

      const container = document.getElementById('aws-captcha-container');
      // 检查容器是否存在
      if (!container) {
        throw new AWSWafError('AWS Captcha container not found', 'container_missing');
      }

      // 清空容器内容，确保干净的渲染环境
      container.innerHTML = '';

      // 添加延迟确保DOM完全准备就绪
      await new Promise((resolve) => setTimeout(resolve, 100));

      try {
        const captchaPromise = createControlledPromise<void>();

        AwsWafCaptcha.renderCaptcha(container, {
          apiKey: process.env.NEXT_PUBLIC_AWS_CAPTCHA_API_KEY,
          disableLanguageSelector: true,
          onFailure: () => {
            captchaPromise.reject(new AWSWafError('AWS Captcha verification failed', 'verification_failed'));
          },
          onSuccess: () => {
            // 验证成功，清空容器并解析 Promise
            if (container?.parentNode) {
              container.innerHTML = '';
            }
            captchaPromise.resolve();
          },
        });

        // 等待验证结果
        return captchaPromise.promise;
      } catch (renderError) {
        throw new AWSWafError(
          renderError instanceof Error ? renderError.message : 'Failed to render AWS Captcha',
          'render_failed',
        );
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          'captcha.error_type': error instanceof AWSWafError ? error.type : 'unknown',
          'captcha.operation': 'captcha',
        },
      });
      throw error;
    }
  };

  return {
    loadCaptchaSuccess: () => {
      isLoaded = true;
    },
    loadCaptchaError: () => {
      loadPromise = null;
      isLoaded = false;
    },
    renderAWSCaptcha,
  };
};
